"use client";

import { useEffect, useMemo, useState } from "react";

import { Layer, Source, useMap } from "react-map-gl/maplibre";

import { publicConfig } from "@/configs/public-config";
import { usePublicTileJson } from "@/hooks/find-my-school/use-public-tile-json";
import {
  DEFAULT_LAYER_MAX_ZOOM,
  LABEL_FONT_SIZE,
  SCHOOL_PIN_LABEL_HIDE_END_ZOOM,
  PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
  PIN_ELEMENTARY_SCHOOL_IMAGE_PATH,
  PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
  PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_PATH,
  PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
  PIN_ELEMENTARY_MIDDLE_IMAGE_PATH,
  PIN_HIGH_SCHOOL_IMAGE_ID,
  PIN_HIGH_SCHOOL_IMAGE_PATH,
  PIN_K8_HIGH_IMAGE_ID,
  PIN_K8_HIGH_IMAGE_PATH,
  PIN_K8_SCHOOL_IMAGE_ID,
  PIN_K8_SCHOOL_IMAGE_PATH,
  PIN_MIDDLE_HIGH_IMAGE_ID,
  PIN_MIDDLE_HIGH_IMAGE_PATH,
  PIN_MIDDLE_SCHOOL_IMAGE_ID,
  PIN_MIDDLE_SCHOOL_IMAGE_PATH,
  PIN_SCHOOL_INACTIVE_IMAGE_ID,
  PIN_SCHOOL_INACTIVE_IMAGE_PATH,
  ACTIVE_PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_ELEMENTARY_SCHOOL_IMAGE_PATH,
  ACTIVE_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
  ACTIVE_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_PATH,
  ACTIVE_PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
  ACTIVE_PIN_ELEMENTARY_MIDDLE_IMAGE_PATH,
  ACTIVE_PIN_HIGH_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_HIGH_SCHOOL_IMAGE_PATH,
  ACTIVE_PIN_K8_HIGH_IMAGE_ID,
  ACTIVE_PIN_K8_HIGH_IMAGE_PATH,
  ACTIVE_PIN_K8_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_K8_SCHOOL_IMAGE_PATH,
  ACTIVE_PIN_MIDDLE_HIGH_IMAGE_ID,
  ACTIVE_PIN_MIDDLE_HIGH_IMAGE_PATH,
  ACTIVE_PIN_MIDDLE_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_MIDDLE_SCHOOL_IMAGE_PATH,
  ACTIVE_PIN_SCHOOL_INACTIVE_IMAGE_ID,
  ACTIVE_PIN_SCHOOL_INACTIVE_IMAGE_PATH,
  POLYGONS_SOURCE_LAYER_ID,
  SCHOOL_BOUNDARIES_LEVEL_SOURCE_LAYER_ID,
  SCHOOL_BOUNDARY_STROKE,
  SCHOOL_COLORS,
  SCHOOL_PINS_SOURCE_LAYER_ID,
  MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
} from "@/providers/maps/constants";
import { useCurrentMapVisualTheme } from "@/providers/maps/hooks/use-current-map-visual-theme";
import { loadMapImages } from "@/providers/maps/utils/load-map-images";
import { buildSchoolPinsIconExpression } from "@/providers/maps/utils/school-pins-icon-expression";
import { schoolTypeContainedInTypesCsvExpression } from "@/providers/maps/utils/school-types-csv";
import { resolveTegolaLayerTiles } from "@/providers/maps/utils/tile-json";
import type { SchoolType } from "@/providers/store/api/find-my-school/find-my-school.interface";

import { useFindMySchoolLayers } from "./find-my-school-layers-context";

import type { DataDrivenPropertyValueSpecification, FilterSpecification } from "maplibre-gl";

const SCHOOL_PIN_IMAGES = [
  { id: PIN_ELEMENTARY_SCHOOL_IMAGE_ID, path: PIN_ELEMENTARY_SCHOOL_IMAGE_PATH },
  { id: PIN_MIDDLE_SCHOOL_IMAGE_ID, path: PIN_MIDDLE_SCHOOL_IMAGE_PATH },
  { id: PIN_HIGH_SCHOOL_IMAGE_ID, path: PIN_HIGH_SCHOOL_IMAGE_PATH },
  { id: PIN_K8_SCHOOL_IMAGE_ID, path: PIN_K8_SCHOOL_IMAGE_PATH },
  { id: PIN_K8_HIGH_IMAGE_ID, path: PIN_K8_HIGH_IMAGE_PATH },
  { id: PIN_MIDDLE_HIGH_IMAGE_ID, path: PIN_MIDDLE_HIGH_IMAGE_PATH },
  { id: PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID, path: PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_PATH },
  { id: PIN_ELEMENTARY_MIDDLE_IMAGE_ID, path: PIN_ELEMENTARY_MIDDLE_IMAGE_PATH },
  { id: PIN_SCHOOL_INACTIVE_IMAGE_ID, path: PIN_SCHOOL_INACTIVE_IMAGE_PATH },
  { id: ACTIVE_PIN_ELEMENTARY_SCHOOL_IMAGE_ID, path: ACTIVE_PIN_ELEMENTARY_SCHOOL_IMAGE_PATH },
  { id: ACTIVE_PIN_MIDDLE_SCHOOL_IMAGE_ID, path: ACTIVE_PIN_MIDDLE_SCHOOL_IMAGE_PATH },
  { id: ACTIVE_PIN_HIGH_SCHOOL_IMAGE_ID, path: ACTIVE_PIN_HIGH_SCHOOL_IMAGE_PATH },
  { id: ACTIVE_PIN_K8_SCHOOL_IMAGE_ID, path: ACTIVE_PIN_K8_SCHOOL_IMAGE_PATH },
  { id: ACTIVE_PIN_K8_HIGH_IMAGE_ID, path: ACTIVE_PIN_K8_HIGH_IMAGE_PATH },
  { id: ACTIVE_PIN_MIDDLE_HIGH_IMAGE_ID, path: ACTIVE_PIN_MIDDLE_HIGH_IMAGE_PATH },
  { id: ACTIVE_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID, path: ACTIVE_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_PATH },
  { id: ACTIVE_PIN_ELEMENTARY_MIDDLE_IMAGE_ID, path: ACTIVE_PIN_ELEMENTARY_MIDDLE_IMAGE_PATH },
  { id: ACTIVE_PIN_SCHOOL_INACTIVE_IMAGE_ID, path: ACTIVE_PIN_SCHOOL_INACTIVE_IMAGE_PATH },
];

const MIN_ZOOM = 8;
const LABEL_LAYER_MIN_ZOOM = Math.max(MIN_ZOOM, SCHOOL_PIN_LABEL_HIDE_END_ZOOM);
const DISTRICT_FILL_COLOR = "#E5E7EB";
const DISTRICT_LINE_COLOR = "#9CA3AF";

const PIN_TYPES = ["ELEMENTARY", "MIDDLE", "HIGH", "K8"] as const;

export interface FindMySchoolVectorLayersProps {
  districtId: string;
  selectedCategory?: SchoolType;
  /** When set, force-show this boundary even if its map level eye is off. */
  highlightedBoundaryId?: string | null;
}

export const FMS_SCHOOL_PINS_SOURCE_ID = "fms-school-pins";
export const FMS_SCHOOL_PINS_LAYER_ID = "fms-school-pins-layer";
export const FMS_SCHOOL_PINS_ACTIVE_LAYER_ID = "fms-school-pins-active-layer";
export const FMS_SCHOOL_PINS_LABEL_LAYER_ID = "fms-school-pins-label-layer";

export const FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID = "fms-map-level-boundaries";
export const FMS_MAP_LEVEL_BOUNDARIES_FILL_LAYER_ID = "fms-map-level-boundaries-fill";
export const FMS_MAP_LEVEL_BOUNDARIES_OUTLINE_LAYER_ID = "fms-map-level-boundaries-outline";

const MAP_LEVEL_FALLBACK_FILL_COLOR = "#6B7280";

export function FindMySchoolVectorLayers({
  districtId,
  selectedCategory = "all",
  highlightedBoundaryId = null,
}: FindMySchoolVectorLayersProps) {
  const mapRef = useMap();
  const { labelTextColor, labelHaloColor, labelHaloWidth } = useCurrentMapVisualTheme(districtId);
  const tileJson = usePublicTileJson(publicConfig.schoolBoundariesUrl);
  const districtsTileJson = usePublicTileJson(publicConfig.boundariesUrl);
  const mapLevelsTileJson = usePublicTileJson(publicConfig.mapLevelsPublishedUrl);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const {
    schoolBoundariesHiddenTypes,
    districtVisible,
    districtSchoolLevelTypes,
    publishedMapLevels,
    mapLevelsHiddenIds,
  } = useFindMySchoolLayers();

  const visibleBoundaryTypes = useMemo(() => {
    const allowed = new Set<string>(districtSchoolLevelTypes);

    return (["ELEMENTARY", "MIDDLE", "HIGH", "K8"] as const)
      .filter((t) => allowed.has(t))
      .filter((t) => !schoolBoundariesHiddenTypes.includes(t));
  }, [districtSchoolLevelTypes, schoolBoundariesHiddenTypes]);

  const visibleMapLevelIds = useMemo(
    () => publishedMapLevels.map((level) => level.id).filter((id) => !mapLevelsHiddenIds.includes(id)),
    [publishedMapLevels, mapLevelsHiddenIds],
  );

  const boundaryFilter = useMemo((): FilterSpecification | undefined => {
    if (visibleBoundaryTypes.length === 0) return undefined;

    return [
      "any",
      ...visibleBoundaryTypes.map(
        (t) => schoolTypeContainedInTypesCsvExpression(t) as unknown as FilterSpecification,
      ),
    ] as unknown as FilterSpecification;
  }, [visibleBoundaryTypes]);

  const mapLevelFilter = useMemo((): FilterSpecification | undefined => {
    const hasVisibleLevels = visibleMapLevelIds.length > 0;
    const hasHighlightedBoundary = Boolean(highlightedBoundaryId);
    if (!hasVisibleLevels && !hasHighlightedBoundary) return undefined;

    if (hasVisibleLevels && hasHighlightedBoundary) {
      return [
        "any",
        ["in", ["get", "mapLevelId"], ["literal", visibleMapLevelIds]],
        ["==", ["get", "id"], highlightedBoundaryId],
      ] as unknown as FilterSpecification;
    }

    if (hasHighlightedBoundary) {
      return ["==", ["get", "id"], highlightedBoundaryId] as unknown as FilterSpecification;
    }

    return [
      "in",
      ["get", "mapLevelId"],
      ["literal", visibleMapLevelIds],
    ] as unknown as FilterSpecification;
  }, [visibleMapLevelIds, highlightedBoundaryId]);

  const schoolBoundariesTiles = useMemo(
    () =>
      resolveTegolaLayerTiles(
        tileJson,
        SCHOOL_BOUNDARIES_LEVEL_SOURCE_LAYER_ID,
        (tile) =>
          `${tile}${tile.includes("?") ? "&" : "?"}district_id=${districtId}&show_on_find_my_school=true`,
        { fallbackToMapTiles: true },
      ),
    [tileJson, districtId],
  );

  const mapLevelBoundariesTiles = useMemo(
    () =>
      resolveTegolaLayerTiles(
        mapLevelsTileJson,
        MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
        (tile) => `${tile}${tile.includes("?") ? "&" : "?"}district_id=${districtId}`,
        { fallbackToMapTiles: true },
      ),
    [mapLevelsTileJson, districtId],
  );

  const districtTiles = useMemo(() => {
    if (!districtsTileJson?.tiles?.length) return [];

    return districtsTileJson.tiles;
  }, [districtsTileJson]);

  const districtFilter = useMemo(
    (): FilterSpecification => (["==", ["get", "id"], districtId] as unknown as FilterSpecification),
    [districtId],
  );

  const schoolPinsTiles = useMemo(() => {
    if (!tileJson || !districtId) return [];

    return resolveTegolaLayerTiles(
      tileJson,
      SCHOOL_PINS_SOURCE_LAYER_ID,
      (tile) =>
        `${tile}${tile.includes("?") ? "&" : "?"}district_id=${districtId}&show_closed_schools=OPEN&show_on_find_my_school=true`,
      { fallbackToMapTiles: true },
    );
  }, [tileJson, districtId]);

  const pinsFilter = useMemo((): FilterSpecification => {
    if (selectedCategory === "all") {
      return [
        "any",
        ...PIN_TYPES.map(
          (t) => schoolTypeContainedInTypesCsvExpression(t) as unknown as FilterSpecification,
        ),
      ] as unknown as FilterSpecification;
    }
    return schoolTypeContainedInTypesCsvExpression(
      selectedCategory,
    ) as unknown as FilterSpecification;
  }, [selectedCategory]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || imagesLoaded) return;
    loadMapImages(map, SCHOOL_PIN_IMAGES)
      .then(() => setImagesLoaded(true))
      .catch((e) => console.error("Failed to load school pin images:", e));
  }, [mapRef, imagesLoaded]);

  const normalIconImageExpression = useMemo(
    (): DataDrivenPropertyValueSpecification<string> =>
      buildSchoolPinsIconExpression({
        variant: "normal",
        emptyTypesFallback: "inactive",
      }) as unknown as DataDrivenPropertyValueSpecification<string>,
    [],
  );

  const activeIconImageExpression = useMemo(
    (): DataDrivenPropertyValueSpecification<string> =>
      buildSchoolPinsIconExpression({
        variant: "active",
        emptyTypesFallback: "inactive",
      }) as unknown as DataDrivenPropertyValueSpecification<string>,
    [],
  );

  const hasSchoolBoundaries = Boolean(tileJson && schoolBoundariesTiles.length > 0);
  const hasDistrictLayer = districtVisible && districtId && districtTiles.length > 0;
  const hasSchoolPinsTiles = schoolPinsTiles.length > 0;
  const hasSchoolPins = hasSchoolPinsTiles && imagesLoaded;
  const hasMapLevelBoundaries =
    mapLevelBoundariesTiles.length > 0 &&
    Boolean(mapLevelFilter) &&
    (publishedMapLevels.length > 0 || Boolean(highlightedBoundaryId));
  const beforeLayerId = mapRef.current?.getLayer(FMS_SCHOOL_PINS_LAYER_ID)
    ? FMS_SCHOOL_PINS_LAYER_ID
    : undefined;
  if (!hasSchoolBoundaries && !hasDistrictLayer && !hasSchoolPinsTiles && !hasMapLevelBoundaries) {
    return null;
  }

  return (
    <>
      {hasDistrictLayer && (
        <Source
          id="fms-district"
          tiles={districtTiles}
          type="vector"
        >
          <Layer
            beforeId={beforeLayerId}
            filter={districtFilter}
            id="fms-district-fill"
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "fill-color": DISTRICT_FILL_COLOR,
              "fill-opacity": 0.25,
            }}
            source="fms-district"
            source-layer={POLYGONS_SOURCE_LAYER_ID}
            type="fill"
          />
          <Layer
            beforeId={beforeLayerId}
            filter={districtFilter}
            id="fms-district-outline"
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "line-color": DISTRICT_LINE_COLOR,
              "line-width": 1.5,
              "line-opacity": 0.9,
            }}
            source="fms-district"
            source-layer={POLYGONS_SOURCE_LAYER_ID}
            type="line"
          />
        </Source>
      )}

      {schoolBoundariesTiles.length > 0 && boundaryFilter && (
        <Source
          id="fms-school-boundaries"
          tiles={schoolBoundariesTiles}
          type="vector"
        >
          <Layer
            beforeId={beforeLayerId}
            filter={boundaryFilter}
            id="fms-school-boundaries-fill"
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "fill-color": [
                "case",
                ["all", ["has", "color"], ["!=", ["get", "color"], ""]],
                ["get", "color"],
                SCHOOL_COLORS.ELEMENTARY,
              ],
              "fill-opacity": 0.2,
            }}
            source="fms-school-boundaries"
            source-layer={SCHOOL_BOUNDARIES_LEVEL_SOURCE_LAYER_ID}
            type="fill"
          />
          <Layer
            beforeId={beforeLayerId}
            filter={boundaryFilter}
            id="fms-school-boundaries-outline"
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "line-color": SCHOOL_BOUNDARY_STROKE.color ?? [
                "case",
                ["all", ["has", "color"], ["!=", ["get", "color"], ""]],
                ["get", "color"],
                SCHOOL_COLORS.ELEMENTARY,
              ],
              "line-width": SCHOOL_BOUNDARY_STROKE.width ?? 1.5,
              "line-opacity": SCHOOL_BOUNDARY_STROKE.opacity ?? 0.8,
            }}
            source="fms-school-boundaries"
            source-layer={SCHOOL_BOUNDARIES_LEVEL_SOURCE_LAYER_ID}
            type="line"
          />
        </Source>
      )}

      {hasMapLevelBoundaries && mapLevelFilter && (
        <Source
          id={FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID}
          promoteId={{ [MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID]: "id" }}
          tiles={mapLevelBoundariesTiles}
          type="vector"
        >
          <Layer
            beforeId={beforeLayerId}
            filter={mapLevelFilter}
            id={FMS_MAP_LEVEL_BOUNDARIES_FILL_LAYER_ID}
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "fill-color": [
                "case",
                ["all", ["has", "color"], ["!=", ["get", "color"], ""]],
                ["get", "color"],
                MAP_LEVEL_FALLBACK_FILL_COLOR,
              ],
              "fill-opacity": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                0.45,
                0.25,
              ],
            }}
            source={FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID}
            source-layer={MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID}
            type="fill"
          />
          <Layer
            beforeId={beforeLayerId}
            filter={mapLevelFilter}
            id={FMS_MAP_LEVEL_BOUNDARIES_OUTLINE_LAYER_ID}
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "line-color": [
                "case",
                ["all", ["has", "color"], ["!=", ["get", "color"], ""]],
                ["get", "color"],
                MAP_LEVEL_FALLBACK_FILL_COLOR,
              ],
              "line-width": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                3,
                1.5,
              ],
              "line-opacity": 0.9,
            }}
            source={FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID}
            source-layer={MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID}
            type="line"
          />
        </Source>
      )}

      {hasSchoolPins && (
        <Source
          id={FMS_SCHOOL_PINS_SOURCE_ID}
          promoteId={{ [SCHOOL_PINS_SOURCE_LAYER_ID]: "id" }}
          tiles={schoolPinsTiles}
          type="vector"
        >
          <Layer
            filter={pinsFilter}
            id={FMS_SCHOOL_PINS_LAYER_ID}
            layout={{
              "icon-image": normalIconImageExpression,
              "icon-size": 24 / 512,
              "icon-anchor": "bottom",
              "icon-allow-overlap": true,
            }}
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "icon-opacity": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                0,
                1,
              ],
            }}
            source={FMS_SCHOOL_PINS_SOURCE_ID}
            source-layer={SCHOOL_PINS_SOURCE_LAYER_ID}
            type="symbol"
          />
          <Layer
            filter={pinsFilter}
            id={FMS_SCHOOL_PINS_ACTIVE_LAYER_ID}
            layout={{
              "icon-image": activeIconImageExpression,
              "icon-size": 24 / 512,
              "icon-anchor": "bottom",
              "icon-allow-overlap": true,
            }}
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={MIN_ZOOM}
            paint={{
              "icon-opacity": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                1,
                0,
              ],
            }}
            source={FMS_SCHOOL_PINS_SOURCE_ID}
            source-layer={SCHOOL_PINS_SOURCE_LAYER_ID}
            type="symbol"
          />
          <Layer
            filter={pinsFilter}
            id={FMS_SCHOOL_PINS_LABEL_LAYER_ID}
            layout={{
              "text-field": ["case", ["has", "name"], ["get", "name"], ""],
              "text-size": LABEL_FONT_SIZE,
              "text-anchor": "top",
              "text-offset": [0, 0.1],
              "text-allow-overlap": false,
              "text-ignore-placement": false,
            }}
            maxzoom={DEFAULT_LAYER_MAX_ZOOM}
            minzoom={LABEL_LAYER_MIN_ZOOM}
            paint={{
              "text-color": labelTextColor,
              "text-halo-color": labelHaloColor,
              "text-halo-width": labelHaloWidth,
            }}
            source={FMS_SCHOOL_PINS_SOURCE_ID}
            source-layer={SCHOOL_PINS_SOURCE_LAYER_ID}
            type="symbol"
          />
        </Source>
      )}
    </>
  );
}
